/**
 * Frontend mirror of the backend metric×window registry — SOURCE OF TRUTH is
 * `backend/src/services/analytics/intentContract.js` (V1_METRICS.supportedWindows,
 * DEFAULT_METRIC_WINDOWS, METRIC_LABELS). validateIntent there is the actual gate; this file
 * is a hand-kept local copy so the frontend workspace never reaches across the monorepo
 * boundary at build/test time (Task 13, #1660). Keep the two in sync by hand — the static
 * test in __tests__/chipConfig.test.js pins every chip's windows against a second local
 * copy of supportedWindows so a drift is caught even without importing the backend file.
 *
 * Pure module — NO React, NO context/service imports — so every helper here is statically
 * testable in this no-jsdom workspace without renderToStaticMarkup.
 */

export const WINDOWS = Object.freeze({
  all_time: { label: 'All time', window: { type: 'all_time' } },
  d7: { label: 'Last 7 days', window: { type: 'rolling_days', days: 7 } },
  d30: { label: 'Last 30 days', window: { type: 'rolling_days', days: 30 } },
  d90: { label: 'Last 90 days', window: { type: 'rolling_days', days: 90 } },
  d365: { label: 'Last 365 days', window: { type: 'rolling_days', days: 365 } },
});

// `windows: null` marks a one-tap chip — it runs immediately via getMetric, it is never a
// mid-selection state, and it has no window sub-chips. `overview` (no fixed `window`) relies on
// the server ignoring window entirely for the compound. The two template-metric chips (Phase 2
// #1662/#1664, Task 10, #1660-follow-on) instead carry a FIXED `window` (and, for hardest_scenario,
// `grain`) — their underlying metric has a locked/registry-default window, not a caller choice, so
// there is no window sub-menu to offer. requestBuilder.js fills/ignores grain for template metrics
// server-side regardless (`spec.grains[0]`) — `grain` here is sent for documentation/consistency
// with the exact body Phase 2's contract names, not because the server needs it.
export const METRIC_CHIPS = Object.freeze([
  { metric: 'overview', label: 'How’s my cohort doing?', windows: null },
  { metric: 'completion_rate', label: 'Completion rate', windows: ['all_time', 'd7', 'd30', 'd90'] },
  { metric: 'avg_score', label: 'Average score', windows: ['all_time', 'd7', 'd30', 'd90'] },
  { metric: 'active_learners', label: 'Active learners', windows: ['d7', 'd30', 'd90'] },
  { metric: 'improvement', label: 'Improvement by attempt', windows: ['d365', 'd90'] },
  { metric: 'mastery_rate', label: 'Mastery rate', windows: ['d365', 'd90'] },
  { metric: 'stuck_learners', label: 'Who looks stuck?', windows: null, window: { type: 'rolling_days', days: 14 } },
  { metric: 'hardest_scenario', label: 'Which scenario is hardest?', windows: null, window: { type: 'all_time' }, grain: 'scenario' },
  // Phase 3 ranking + window-comparison suggestion chips (human-test round 1, #1666/#1667) —
  // FLAT one-tap entries mirroring the stuck_learners/hardest_scenario shape above (windows:null
  // + a fixed `window`, no sub-menu). Chip hierarchy/grouping is explicitly DEFERRED to #1708
  // (product-owner decision) — these are plain rows in the same chip strip, not a new grouped
  // mechanism.
  //
  // The two ranking chips reuse avg_score's learner grain — the ONLY metric×grain combo
  // intentContract.js's `supportsRanking` opens (see that file's header) — with an explicit
  // `ranking` selector; the comparison chip pins avg_score to a 30-day rolling window with
  // `compare:'previous_window'` (requestBuilder.js's Task 9 enum). All three now share
  // metric:'avg_score' with the original "Average score" chip above, so each carries a distinct
  // `id` — AnalyticsAsk.jsx keys/highlights chips off `id ?? metric` (chip.metric alone is no
  // longer a unique key once more than one chip shares a metric).
  { id: 'avg_score_top5', metric: 'avg_score', label: 'Who are the top 5 learners by score?', windows: null, window: { type: 'all_time' }, grain: 'learner', ranking: { by: 'avg_score', direction: 'desc', limit: 5 } },
  { id: 'avg_score_stale', metric: 'avg_score', label: 'Who hasn’t practised recently?', windows: null, window: { type: 'all_time' }, grain: 'learner', ranking: { by: 'last_active', direction: 'asc' } },
  { id: 'avg_score_compare_prev', metric: 'avg_score', label: 'How does this month compare to last?', windows: null, window: { type: 'rolling_days', days: 30 }, compare: 'previous_window' },
]);

export function findChip(metric) {
  return METRIC_CHIPS.find((c) => c.metric === metric) ?? null;
}

/** The window chip keys to render beneath the chip row (+ implicit Cancel), or null when no
 *  metric is mid-selection / the metric has no window choice. */
export function windowChipsFor(selectedMetricForWindow) {
  if (!selectedMetricForWindow) return null;
  return findChip(selectedMetricForWindow)?.windows ?? null;
}

/** What `selectedMetricForWindow` should become after tapping a metric chip (gemini plan-review
 *  P2): a `windows:null` chip (overview) always runs immediately and is never "selected"; any
 *  other metric chip — including tapping a DIFFERENT metric while one is already selected —
 *  resets selection to the newly-tapped metric. */
export function nextSelectedMetric(tappedMetric) {
  const chip = findChip(tappedMetric);
  if (!chip || !chip.windows) return null;
  return tappedMetric;
}

export function windowLabel(windowKey) {
  return WINDOWS[windowKey]?.label ?? windowKey;
}

/** Readable "Metric — Window" label used to fill the input box on a window-chip tap
 *  (gemini P2.6 — every chip tap fills the box with its readable label). */
export function metricWindowLabel(metric, windowKey) {
  const chip = findChip(metric);
  return `${chip?.label ?? metric} — ${windowLabel(windowKey)}`;
}

/** Beta banner copy (one line under the input). Pure so it's testable without rendering
 *  AnalyticsAsk.jsx (which pulls useBranding → the i18n/document chain). Praxis-safe (no
 *  hardcoded "Tacit"), no URLs. */
export function betaBannerText(appName) {
  return `${appName} analytics is growing — if it can’t answer something yet, it logs the question and we prioritise it.`;
}

/** Maps the container's `lastRequest` (set by every submit path — ask() or runMetric()) to
 *  the exact replay call Retry must reissue (codex P1.1). A failed deterministic /metric
 *  request (chip / window / clarify option / nearest tap) must retry through /metric again,
 *  NOT through /ask with the chip's rendered LABEL as a freeform question — different API,
 *  different semantics, possibly an `unsupported` outcome for a label like
 *  "Completion rate — Last 30 days". Pure so it's statically testable without rendering the
 *  (impure, fetch-owning) container. Returns null when there is nothing to replay yet. */
export function buildRetryCall(lastRequest) {
  if (!lastRequest) return null;
  if (lastRequest.mode === 'metric') return { mode: 'metric', payload: lastRequest.payload };
  return { mode: 'ask', question: lastRequest.question };
}

/** Maps a clarify option (server-authored — see scopeClarify.js/clarifyOptions.js) to the
 *  /metric payload AnalyticsAsk.jsx's runMetric() sends. A scope/entity clarify option's body now
 *  carries `scope`, `grain`, and `compare` alongside `metric`/`window` — Task 10 (#1660-follow-on)
 *  plus Task 11 fix wave #2 (codex review P1b.4) require ALL of them survive the pick, not just
 *  {metric, window} (the pre-Phase-2 shape). `compare` was the one dropped field (a group chip
 *  authored for a comparison draft would silently lose its 'vs_rest' flag on replay, downgrading a
 *  comparison ask into a plain group figure) — fixed here. Pure so it's statically testable without
 *  rendering the (impure, fetch-owning) container — mirrors buildRetryCall's extraction for the
 *  same reason.
 *
 *  Deliberately does NOT read/forward `preset` (#1694 Task 9, #1692 drift pin in
 *  __tests__/chipConfig.test.js): a scope/entity clarify option is authored from a draft
 *  (scopeClarify.js/clarifyOptions.js) that never carries a preset — presets only exist on
 *  template-table `presentTable` output. A preset chip replays through
 *  `handlePresetChange`'s verbatim `follow_up.body` instead, never through this clarify-pick
 *  path, so there is nothing here to forward and this mapping must not invent the field. */
export function clarifyPickPayload(option) {
  return { metric: option.metric, window: option.window, grain: option.grain, scope: option.scope, compare: option.compare };
}
